import React, { useMemo, useState } from 'react'
import { Autocomplete, Box, IconButton, MenuItem, TextField, Tooltip, Typography } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import CloseIcon from '@mui/icons-material/Close'
import { useSnackbar } from 'notistack'
import _ from 'lodash'
import palette from '../../../../theme/palette'
import { useGetAllActiveMerchantsIdAndNameCachedQuery } from '../../../redux/commonApiCalls'
import { useNabdMapMerchantMutation, useNabdMastersQuery, useNabdUnmapMerchantMutation } from '../../../redux/ptlNabdApiSlice'
import { canAct } from '../constants'
import { inputSx } from '../eddBreachUi'

/**
 * Merchant → KAM mapping (artifact "Merchant to KAM mapping"): one row per mapped merchant with a KAM picker and a
 * remove button, then an add row (unmapped PTL merchant + active KAM → Map). A client-attributable delay pre-selects
 * the KAM mapped here. Used on the PTL Team Master page and in the drawer on the attribution page.
 * `canEdit` = PTL_EDD_ADMIN; without it the list is read-only.
 */
export default function KamMappingEditor({ masters: mastersProp, canEdit }) {
  const { enqueueSnackbar } = useSnackbar()
  // always the latest mapping: re-fetched every time the editor opens (drawer or page), so a merchant mapped elsewhere
  // never shows as unmapped here
  const { data: fresh } = useNabdMastersQuery(undefined, { refetchOnMountOrArgChange: true })
  const masters = fresh || mastersProp
  const { data: merchants = [] } = useGetAllActiveMerchantsIdAndNameCachedQuery()
  const [mapMerchant, { isLoading: mapping }] = useNabdMapMerchantMutation()
  const [unmap] = useNabdUnmapMerchantMutation()
  const [newMerchant, setNewMerchant] = useState(null)
  const [newKam, setNewKam] = useState('')

  const kams = useMemo(() => (masters?.team || []).filter((t) => canAct(t, 'KAM') && t.active).sort((a, b) => a.name.localeCompare(b.name)), [masters])
  const rows = useMemo(() => _.orderBy(masters?.kamMapping || [], (m) => (m.merchantName || '').toLowerCase()), [masters])
  const mappedOids = useMemo(() => new Set(rows.map((r) => r.merchantOid)), [rows])
  const unmapped = useMemo(() => _.orderBy(
    merchants.filter((m) => m.is_deleted === '0' && m.isPTLMerchant === true && !mappedOids.has(m.id)), (m) => (m.name || '').toLowerCase(),
  ), [merchants, mappedOids])

  const fail = (e) => enqueueSnackbar(e?.data?.message || e?.message || 'Something went wrong', { variant: 'error' })

  const change = async (row, kamMemberId) => {
    try {
      await mapMerchant({ merchantOid: row.merchantOid, merchantName: row.merchantName, kamMemberId }).unwrap()
      enqueueSnackbar('Mapping updated', { variant: 'success' })
    } catch (e) { fail(e) }
  }
  const remove = async (row) => {
    try {
      await unmap(row.merchantOid).unwrap()
      enqueueSnackbar(`Mapping removed for ${row.merchantName}`, { variant: 'success' })
    } catch (e) { fail(e) }
  }
  const add = async () => {
    if (!newMerchant || !newKam) { enqueueSnackbar('Pick a merchant and a Key Account Manager', { variant: 'warning' }); return }
    try {
      await mapMerchant({ merchantOid: newMerchant.id, merchantName: newMerchant.name, kamMemberId: newKam }).unwrap()
      enqueueSnackbar(`${newMerchant.name} mapped to ${kams.find((k) => k.id === newKam)?.name}`, { variant: 'success' })
      setNewMerchant(null)
      setNewKam('')
    } catch (e) { fail(e) }
  }

  return (
    <Box>
      <Box sx={{ maxHeight: 360, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {rows.length === 0 && <Typography sx={{ fontSize: 12, color: 'text.secondary', p: 0.5 }}>No merchant is mapped yet.</Typography>}
        {rows.map((r) => {
          const kamActive = kams.some((k) => k.id === r.kamMemberId)
          return (
            <Box key={r.merchantOid} sx={{ display: 'grid', gridTemplateColumns: '1fr 220px 32px', gap: 1, alignItems: 'center',
              p: '6px 8px', borderRadius: '6px', border: `1px solid ${palette.grey[300]}`, bgcolor: 'background.paper' }}>
              <Typography sx={{ fontSize: 12.5, fontWeight: 600 }} noWrap title={r.merchantName}>{r.merchantName || r.merchantOid}</Typography>
              <TextField select size='small' sx={inputSx} value={kamActive ? r.kamMemberId : ''} disabled={!canEdit}
                onChange={(e) => change(r, e.target.value)} SelectProps={{ displayEmpty: true }} aria-label={`Key Account Manager for ${r.merchantName}`}>
                <MenuItem value='' disabled sx={{ fontSize: 13 }}>KAM inactive, pick another</MenuItem>
                {kams.map((k) => <MenuItem key={k.id} value={k.id} sx={{ fontSize: 13 }}>{k.name}</MenuItem>)}
              </TextField>
              {canEdit ? (
                <Tooltip title='Remove mapping'>
                  <IconButton size='small' onClick={() => remove(r)} aria-label={`Remove mapping for ${r.merchantName}`}><CloseIcon fontSize='small' /></IconButton>
                </Tooltip>
              ) : <span />}
            </Box>
          )
        })}
      </Box>
      {canEdit && (
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 220px auto', gap: 1, mt: 1.5, alignItems: 'center', maxWidth: 720 }}>
          <Autocomplete size='small' options={unmapped} value={newMerchant} onChange={(_e, v) => setNewMerchant(v)}
            getOptionLabel={(m) => m.name || ''} isOptionEqualToValue={(a, b) => a.id === b.id} ListboxProps={{ sx: { fontSize: 13 } }}
            noOptionsText='All PTL merchants are mapped'
            renderInput={(p) => <TextField {...p} sx={inputSx} placeholder='Merchant' />} />
          <TextField select size='small' sx={inputSx} value={newKam} onChange={(e) => setNewKam(e.target.value)} SelectProps={{ displayEmpty: true }}>
            <MenuItem value='' disabled sx={{ fontSize: 13 }}>Key Account Manager</MenuItem>
            {kams.map((k) => <MenuItem key={k.id} value={k.id} sx={{ fontSize: 13 }}>{k.name}</MenuItem>)}
          </TextField>
          <LoadingButton variant='contained' size='small' loading={mapping} onClick={add} sx={{ height: 40 }}>Map</LoadingButton>
        </Box>
      )}
    </Box>
  )
}
